import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';

export function useCollaboratorManagement() {
  const { novel, selectedNovel } = useNovel();
  if (!novel.id || !selectedNovel) {
    throw new Error('useCollaboratorManagement must be used within a NovelProvider with a loaded novel.');
  }

  const { novelId } = selectedNovel;

  const { t } = useTranslation();
  const { backend, user } = useFirebase();
  const { updateNovelItem } = useNovelRepository();

  const [newCollabEmail, setNewCollabEmail] = useState('');
  const [collabError, setCollabError] = useState<string | null>(null);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);

  const isUpdatingCollab = updatingMemberId !== null;

  // TODO: revise this shit
  const handleAddCollaborator = async () => {
    if (!newCollabEmail.trim() || !backend) return;
    const queryTerm = newCollabEmail.trim();

    if (user?.email && queryTerm.toLowerCase() === user.email.toLowerCase()) {
      setCollabError(t('dashboard.settings.cannot_add_self'));
      return;
    }
    if (user?.displayName && queryTerm.toLowerCase() === user.displayName.toLowerCase()) {
      setCollabError(t('dashboard.settings.cannot_add_self'));
      return;
    }

    setCollabError(null);
    setUpdatingMemberId('adding');

    try {
      let resolvedUid: string | null = null;

      try {
        const res = await backend.novels.resolveUserByQuery(queryTerm);
        resolvedUid = res.uid;
      } catch (err: unknown) {
        if (
          err &&
          typeof err === 'object' &&
          'code' in err &&
          err.code === 'functions/not-found'
        ) {
          if (queryTerm.includes('@')) {
            const email = queryTerm.toLowerCase();
            const currentPending = novel.pendingCollaborators || [];
            if (currentPending.includes(email)) {
              setCollabError(t('dashboard.settings.already_invited'));
              setUpdatingMemberId(null);
              return;
            }
            try {
              await backend.novels.sendEmailInvitation({
                email,
                novelId: novelId || '',
                ownerId: novel.ownerId,
                role: 'collaborator',
                novelTitle: novel.title || 'Untitled Novel',
              });
            } catch (emailErr) {
              console.error('Failed to trigger email invitation dispatch:', emailErr);
            }
            setNewCollabEmail('');
            setUpdatingMemberId(null);
            return;
          } else {
            setCollabError(t('dashboard.settings.display_name_not_found'));
            setUpdatingMemberId(null);
            return;
          }
        }
        throw err;
      }

      if (resolvedUid === novel.ownerId) {
        setCollabError(t('dashboard.settings.user_is_owner'));
        return;
      }

      const collaboratorsMap = novel.collaborators || {};
      if (resolvedUid in collaboratorsMap) {
        setCollabError(t('dashboard.settings.already_collab'));
        return;
      }

      // Add novel member securely via Cloud Function to keep interacted list in sync!
      await backend.novels.addNovelMember({ novelId: novelId || '', memberUid: resolvedUid, role: 'collaborator' });
      setNewCollabEmail('');
    } catch (err: unknown) {
      console.error('Failed to add collaborator:', err);
      setCollabError(t('dashboard.settings.add_error'));
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveCollaborator = async (collabId: string) => {
    if (!backend) return;
    setUpdatingMemberId(collabId);
    try {
      await backend.novels.removeNovelMember({
        ownerId: novel.ownerId,
        novelId: novelId || '',
        memberUid: collabId,
        role: 'collaborator',
      });
    } catch (err) {
      console.error('Failed to remove collaborator:', err);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemovePendingCollaborator = async (email: string) => {
    if (!backend) return;
    setUpdatingMemberId(email);
    try {
      await backend.novels.removeNovelMember({
        ownerId: novel.ownerId,
        novelId: novelId || '',
        email,
        role: 'collaborator',
      });
    } catch (err) {
      console.error('Failed to remove pending collaborator:', err);
    } finally {
      setUpdatingMemberId(null);
    }
  };

  return {
    newCollabEmail,
    setNewCollabEmail,
    collabError,
    isUpdatingCollab,
    updatingMemberId,
    handleAddCollaborator,
    handleRemoveCollaborator,
    handleRemovePendingCollaborator,
  };
}
